import React, { FunctionComponent, useState, ChangeEvent } from 'react';
import { Step1ValueModel } from '../types';
import InputText from '@sharedComponents/InputText';
import InputCheckbox from '@sharedComponents/InputCheckbox';
import { useTranslation } from 'react-i18next';
import { validateStep1 } from '../Validation';
import { validateOrgNrOrVatNr } from '../Validation';
import {
    validateOrganisationNumber,
    OrganisationType,
} from '../../shared/Validation';
import some from 'lodash/some';
import { Step1Props } from './types';
import { NumberType } from '../types';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import Icon from "@sharedComponents/Icon";
import Panel from "@sharedComponents/Panel";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import { getAllErrors } from "@sharedComponents/PantaForm/utils";
import Alert from "@sharedComponents/Alert";

const Step1: FunctionComponent<Step1Props> = props => {
    const { t } = useTranslation()
    const [stepValues, setStepValues] = useState<Step1ValueModel>({
        organizationNumber: '',
        vatNumber: '',
        optionalConnection: false,
    })
    const [loading, setLoading] = useState<boolean>(false)
    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])
    const allErrors = getAllErrors(formErrors)

    const onChangeOrgNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim()
        setStepValues({
            ...stepValues,
            organizationNumber: value,
            numberType: value
                ? NumberType.OrganisationNumber
                : NumberType.VATNumber,
        })
        props.onChangeValues &&
            props.onChangeValues({
                organizationNumber: value,
                numberType: value
                    ? NumberType.OrganisationNumber
                    : NumberType.VATNumber,
            })

        const errors = validateOrgNrOrVatNr(
            value,
            stepValues.vatNumber,
            formErrors
        )
        validateOrganisationNumber(
            OrganisationType.Supplier,
            value,
            errors,
            false,
            false
        ).then(result => {
            setFormErrors(result?.errors)
        })
    }

    const onChangeVatNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({
            ...stepValues,
            vatNumber: value,
            numberType: value
                ? NumberType.VATNumber
                : NumberType.OrganisationNumber,
        })
        props.onChangeValues &&
            props.onChangeValues({
                vatNumber: value,
                numberType: value
                    ? NumberType.VATNumber
                    : NumberType.OrganisationNumber,
            })
        const errors = validateOrgNrOrVatNr(
            stepValues.organizationNumber,
            value,
            formErrors
        )
        setFormErrors(errors)
    }

    const onChangeOptionalConnection = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked
        setStepValues({ ...stepValues, optionalConnection: value })
        props.onChangeValues &&
            props.onChangeValues({ optionalConnection: value })
    }

    const nextButtonText = (
        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
            <span>{t('button.next')}</span>
            <Icon iconName="arrow-right" />
        </span>
    )

    const nextStep = () => {
        setLoading(true)
        validateStep1(stepValues, formErrors)
            .then((errors: FormErrorType[]) => {
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else {
                    setFormErrors([])

                    props.onChangeValues &&
                        props.onChangeValues({
                            requestInfo: true,
                        })
                    props.nextStep && props.nextStep()
                }
                setLoading(false)
            })
            .catch(() => {
                setLoading(false)
            })
    }

    const onCancel = () => {
        setFormErrors([])
        props.onCancel && props.onCancel()
    }

    const cancelButtonText = (
        <span className='buttons-wrapper__button-text'>
            <Icon iconName="close" />
            <span>{t('button.cancel')}</span>
        </span>
    )

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='form-step-wrapper'>
            <h3>{t('companyRegistration.stepLabel.step1')}</h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>

            <div className='fields-wrapper'>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="crb-orgnr"
                        label={t('companyRegistration.organisationNumber')}
                        value={stepValues.organizationNumber || ''}
                        required={true}
                        hasError={
                            !!some(formErrors, ['key', 'organizationNumber'])
                        }
                        onChange={onChangeOrgNumber}
                        autoComplete="off"
                        errorText={uniqueErrors('organizationNumber')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="crb-vat-nr"
                        label={t('companyRegistration.vatNumber')}
                        value={stepValues.vatNumber || ''}
                        required={false}
                        hasError={!!some(formErrors, ['key', 'vatNumber'])}
                        onChange={onChangeVatNumber}
                        autoComplete="off"
                    />
                </div>
            </div>

            {props.cmsContent ? (
                <>
                    <h4>
                        {t('companyRegistration.optionalConnection.heading')}
                    </h4>
                    <div className='optional-connection-wrapper'>
                        <Panel type="info" className="optional-connection-info">
                            <div dangerouslySetInnerHTML={{__html: props.cmsContent}}/>
                        </Panel>
                        <InputCheckbox
                            id="optional-connection"
                            label={t(
                                'companyRegistration.optionalConnection.chkLabel'
                            )}
                            onChange={onChangeOptionalConnection}
                            checked={stepValues.optionalConnection || false}
                        />
                    </div>
                </>
            ) : (
                ''
            )}
            {allErrors.length > 0 && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={nextButtonText}
                    onClick={nextStep}
                    disabled={loading}
                    loading={loading}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={cancelButtonText}
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step1